import { Icon } from "@iconify/react";
import { motion, useReducedMotion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { buildAppLink } from "../../../utils/landingRedirect";

type FeatureKey = "record" | "import" | "agent" | "create" | "study";

type DemoStep = {
  title: string;
  body: string;
};

type DemoFeature = {
  key: FeatureKey;
  icon: string;
  color: string;
  softColor: string;
  label: string;
  title: string;
  kicker: string;
  body: string;
  prompt: string;
  sources: string[];
  steps: DemoStep[];
  outputs: string[];
};

const featureOrder: FeatureKey[] = ["record", "import", "agent", "create", "study"];

const featureMeta: Record<FeatureKey, { icon: string; color: string; softColor: string }> = {
  record: { icon: "lucide:audio-lines", color: "#4ea4f5", softColor: "#eaf5ff" },
  import: { icon: "lucide:upload-cloud", color: "#14b8a6", softColor: "#e6fffb" },
  agent: { icon: "lucide:bot", color: "#7c3aed", softColor: "#f3edff" },
  create: { icon: "lucide:wand-sparkles", color: "#f97316", softColor: "#fff3e8" },
  study: { icon: "lucide:graduation-cap", color: "#22c55e", softColor: "#eafaf0" },
};

const ConsumerWorkflowDemo: React.FC = () => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const spotlightRef = useRef<HTMLDivElement>(null);
  const [activeKey, setActiveKey] = useState<FeatureKey>("record");
  const [activeStep, setActiveStep] = useState(0);

  const copy = (key: string, fallback: string) =>
    String(t(`modern.workBrain.consumerDemo.${key}`, fallback) ?? fallback);

  const features = useMemo<DemoFeature[]>(
    () =>
      featureOrder.map((key) => {
        const meta = featureMeta[key];
        return {
          key,
          icon: meta.icon,
          color: meta.color,
          softColor: meta.softColor,
          label: copy(`features.${key}.label`, fallbackCopy[key].label),
          title: copy(`features.${key}.title`, fallbackCopy[key].title),
          kicker: copy(`features.${key}.kicker`, fallbackCopy[key].kicker),
          body: copy(`features.${key}.body`, fallbackCopy[key].body),
          prompt: copy(`features.${key}.prompt`, fallbackCopy[key].prompt),
          sources: [
            copy(`features.${key}.sources.0`, fallbackCopy[key].sources[0]),
            copy(`features.${key}.sources.1`, fallbackCopy[key].sources[1]),
            copy(`features.${key}.sources.2`, fallbackCopy[key].sources[2]),
          ],
          steps: [
            {
              title: copy(`features.${key}.steps.0.title`, fallbackCopy[key].steps[0].title),
              body: copy(`features.${key}.steps.0.body`, fallbackCopy[key].steps[0].body),
            },
            {
              title: copy(`features.${key}.steps.1.title`, fallbackCopy[key].steps[1].title),
              body: copy(`features.${key}.steps.1.body`, fallbackCopy[key].steps[1].body),
            },
            {
              title: copy(`features.${key}.steps.2.title`, fallbackCopy[key].steps[2].title),
              body: copy(`features.${key}.steps.2.body`, fallbackCopy[key].steps[2].body),
            },
          ],
          outputs: [
            copy(`features.${key}.outputs.0`, fallbackCopy[key].outputs[0]),
            copy(`features.${key}.outputs.1`, fallbackCopy[key].outputs[1]),
            copy(`features.${key}.outputs.2`, fallbackCopy[key].outputs[2]),
          ],
        };
      }),
    [t]
  );

  const activeFeature = features.find((feature) => feature.key === activeKey) || features[0];

  useEffect(() => {
    setActiveStep(0);
  }, [activeKey]);

  useEffect(() => {
    if (reduceMotion) return undefined;

    const timer = window.setInterval(() => {
      setActiveStep((current) => (current + 1) % 3);
    }, 2600);

    return () => window.clearInterval(timer);
  }, [reduceMotion]);

  const updateSpotlight = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    target.style.setProperty("--spotlight-x", `${event.clientX - rect.left}px`);
    target.style.setProperty("--spotlight-y", `${event.clientY - rect.top}px`);
  };

  return (
    <section
      id="workflow-demo"
      className="landing-snap-section flex min-h-[100dvh] scroll-mt-28 items-center bg-[#fbfdff] px-4 pb-20 pt-28 md:pb-24 md:pt-32"
    >
      <div className="mx-auto w-full max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-cornflowerblue shadow-sm ring-1 ring-blue-100"
          >
            <Icon icon="lucide:mouse-pointer-click" className="text-base" />
            {copy("badge", "Interactive product walkthrough")}
          </motion.div>
          <motion.h2
            initial={reduceMotion ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="mt-5 text-[30px] font-bold leading-tight text-gray-950 md:text-[48px]"
          >
            {copy("title", "See what YouWo can do in one pass.")}
          </motion.h2>
          <motion.p
            initial={reduceMotion ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="mx-auto mt-4 max-w-2xl text-base leading-7 text-gray-600 md:text-lg"
          >
            {copy(
              "body",
              "Record, import, ask an agent, create polished outputs, and study from the same source-backed memory."
            )}
          </motion.p>
        </div>

        <motion.div
          ref={spotlightRef}
          onPointerMove={updateSpotlight}
          initial={reduceMotion ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.18 }}
          transition={{ duration: 0.45, delay: 0.12 }}
          className="consumer-demo-spotlight mt-10 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
        >
          <div className="relative z-10 grid gap-0 lg:grid-cols-[0.82fr_1.08fr_0.9fr]">
            <div className="border-b border-gray-100 bg-[#f8fafc] p-4 md:p-5 lg:border-b-0 lg:border-r">
              <div className="mb-4 flex items-center justify-between gap-3">
                <p className="text-sm font-bold text-gray-950">
                  {copy("featureLabel", "Choose a workflow")}
                </p>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-500 ring-1 ring-gray-200">
                  {copy("hoverHint", "Hover or tap")}
                </span>
              </div>

              <div className="grid gap-2">
                {features.map((feature) => {
                  const isActive = feature.key === activeKey;
                  return (
                    <button
                      key={feature.key}
                      type="button"
                      onClick={() => setActiveKey(feature.key)}
                      onFocus={() => setActiveKey(feature.key)}
                      onMouseEnter={() => setActiveKey(feature.key)}
                      className={`group flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-left transition-all duration-200 ${
                        isActive
                          ? "border-cornflowerblue bg-white shadow-sm"
                          : "border-transparent bg-transparent hover:border-gray-200 hover:bg-white"
                      }`}
                    >
                      <span
                        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
                        style={{ backgroundColor: feature.softColor, color: feature.color }}
                      >
                        <Icon icon={feature.icon} className="text-xl" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-gray-950">{feature.label}</span>
                        <span className="mt-1 block text-xs leading-5 text-gray-500">{feature.kicker}</span>
                      </span>
                      <Icon
                        icon="lucide:arrow-right"
                        className={`ml-auto mt-2 text-sm transition-transform ${
                          isActive ? "translate-x-0 text-cornflowerblue" : "-translate-x-1 text-gray-300 group-hover:text-gray-500"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-center bg-white p-4 md:p-6">
              <PhoneWorkflowPreview feature={activeFeature} activeStep={activeStep} reduceMotion={Boolean(reduceMotion)} />
            </div>

            <div className="border-t border-gray-100 bg-[#f8fafc] p-4 md:p-5 lg:border-l lg:border-t-0">
              <div>
                <div
                  className="mb-3 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-bold"
                  style={{ backgroundColor: activeFeature.softColor, color: activeFeature.color }}
                >
                  <Icon icon={activeFeature.icon} />
                  {activeFeature.label}
                </div>
                <h3 className="text-2xl font-bold leading-tight text-gray-950">{activeFeature.title}</h3>
                <p className="mt-3 text-sm leading-6 text-gray-600">{activeFeature.body}</p>
              </div>

              <div className="mt-5 rounded-lg bg-gray-950 p-4 text-white">
                <p className="text-xs font-bold text-blue-200">{copy("promptLabel", "Example prompt")}</p>
                <p className="mt-2 text-sm leading-6 text-gray-100">{activeFeature.prompt}</p>
              </div>

              <div className="mt-5">
                <p className="text-sm font-bold text-gray-950">{copy("stepLabel", "Workflow steps")}</p>
                <div className="mt-3 space-y-2">
                  {activeFeature.steps.map((step, index) => {
                    const isActive = index === activeStep;
                    return (
                      <button
                        key={step.title}
                        type="button"
                        onClick={() => setActiveStep(index)}
                        className={`w-full cursor-pointer rounded-lg border p-3 text-left transition-colors ${
                          isActive ? "border-cornflowerblue bg-white" : "border-gray-200 bg-white/70 hover:bg-white"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-bold text-gray-950">{step.title}</span>
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: isActive ? activeFeature.color : "#d1d5db" }}
                          />
                        </div>
                        <p className="mt-1 text-xs leading-5 text-gray-500">{step.body}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5">
                <p className="text-sm font-bold text-gray-950">{copy("outputLabel", "What comes out")}</p>
                <div className="mt-3 grid gap-2">
                  {activeFeature.outputs.map((output) => (
                    <div key={output} className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm text-gray-700 ring-1 ring-gray-200">
                      <Icon icon="lucide:check" className="text-cornflowerblue" />
                      <span>{output}</span>
                    </div>
                  ))}
                </div>
              </div>

              <a
                href={buildAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gray-950 px-4 py-3 text-sm font-bold text-white no-underline transition-colors hover:bg-gray-800"
              >
                {copy("primaryCta", "Try it with your content")}
                <Icon icon="lucide:arrow-up-right" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const PhoneWorkflowPreview: React.FC<{
  feature: DemoFeature;
  activeStep: number;
  reduceMotion: boolean;
}> = ({ feature, activeStep, reduceMotion }) => {
  return (
    <div className="w-full max-w-[430px]">
      <div className="mx-auto rounded-[28px] border border-gray-200 bg-gray-950 p-2 shadow-2xl">
        <div className="overflow-hidden rounded-[22px] bg-[#fbfdff]">
          <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
            <div className="flex items-center gap-2">
              <img src="/logo/logo.png" alt="YouWo" className="h-8 w-8 rounded-lg object-cover" />
              <div>
                <p className="text-sm font-bold text-gray-950">YouWo</p>
                <p className="text-[11px] text-gray-500">{feature.label}</p>
              </div>
            </div>
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg"
              style={{ backgroundColor: feature.softColor, color: feature.color }}
            >
              <Icon icon={feature.icon} className="text-lg" />
            </div>
          </div>

          <div className="min-h-[470px] p-4">
            <div className="flex flex-wrap gap-2">
              {feature.sources.map((source) => (
                <span key={source} className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 ring-1 ring-gray-200">
                  {source}
                </span>
              ))}
            </div>

            <div className="mt-4">
              {feature.key === "record" && <RecordPreview feature={feature} activeStep={activeStep} reduceMotion={reduceMotion} />}
              {feature.key === "import" && <ImportPreview feature={feature} activeStep={activeStep} />}
              {feature.key === "agent" && <AgentPreview feature={feature} activeStep={activeStep} />}
              {feature.key === "create" && <CreatePreview feature={feature} activeStep={activeStep} />}
              {feature.key === "study" && <StudyPreview feature={feature} activeStep={activeStep} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RecordPreview: React.FC<{ feature: DemoFeature; activeStep: number; reduceMotion: boolean }> = ({
  feature,
  activeStep,
  reduceMotion,
}) => (
  <div className="space-y-3">
    <div className="rounded-lg bg-white p-4 ring-1 ring-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-gray-950">{feature.steps[0].title}</p>
          <p className="text-xs text-gray-500">00:42</p>
        </div>
        <span className="flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-xs font-bold text-red-600">
          <span className={`h-2 w-2 rounded-full bg-red-500 ${reduceMotion ? "" : "animate-pulse"}`} />
          Live
        </span>
      </div>
      <div className="mt-4 flex h-14 items-end justify-center gap-1.5">
        {[18, 34, 24, 46, 30, 52, 28, 42, 22, 36, 20, 48].map((height, index) => (
          <span
            key={`${height}-${index}`}
            className="w-2 rounded-full"
            style={{
              height,
              backgroundColor: activeStep === 0 ? feature.color : "#bfdbfe",
              opacity: activeStep === 0 || index % 3 === 0 ? 1 : 0.55,
            }}
          />
        ))}
      </div>
    </div>

    <div className="rounded-lg bg-white p-4 ring-1 ring-gray-200">
      <p className="text-xs font-bold text-gray-400">01:12</p>
      <p className="mt-2 text-sm leading-6 text-gray-800">
        The professor says this theorem is the bridge between gradient descent and model generalization.
      </p>
      <p className="mt-2 text-sm italic leading-6 text-[#226fb7]">
        这条定理连接了梯度下降与模型泛化。
      </p>
    </div>

    <div className={`rounded-lg border p-4 ${activeStep >= 2 ? "border-cornflowerblue bg-blue-50" : "border-gray-200 bg-white"}`}>
      <div className="flex items-center gap-2">
        <Icon icon="lucide:sparkles" className="text-cornflowerblue" />
        <p className="text-sm font-bold text-gray-950">Ask while recording</p>
      </div>
      <p className="mt-2 text-sm leading-6 text-gray-600">Turn this into exam notes with definitions and examples.</p>
    </div>
  </div>
);

const ImportPreview: React.FC<{ feature: DemoFeature; activeStep: number }> = ({ feature, activeStep }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-2">
      {[
        ["lucide:file-text", "Lecture.pdf"],
        ["logos:youtube-icon", "Video link"],
        ["lucide:image", "Whiteboard"],
        ["lucide:music", "Audio file"],
      ].map(([icon, label], index) => (
        <div
          key={label}
          className={`rounded-lg border bg-white p-3 ${activeStep === 0 || index < 2 ? "border-cornflowerblue/40" : "border-gray-200"}`}
        >
          <Icon icon={icon} className="text-xl" style={{ color: index === 1 ? "#ff0000" : feature.color }} />
          <p className="mt-2 text-xs font-bold text-gray-950">{label}</p>
        </div>
      ))}
    </div>

    <div className="rounded-lg bg-white p-4 ring-1 ring-gray-200">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-gray-950">{feature.steps[1].title}</p>
        <span className="text-xs font-semibold text-cornflowerblue">4 sources</span>
      </div>
      <div className="mt-3 space-y-2">
        {["Definitions", "Worked examples", "Questions to review"].map((item, index) => (
          <div key={item} className="flex items-center gap-2 rounded-lg bg-[#f8fafc] px-3 py-2">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: index <= activeStep ? feature.color : "#cbd5e1" }} />
            <span className="text-xs font-semibold text-gray-700">{item}</span>
          </div>
        ))}
      </div>
    </div>

    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-xs font-bold text-gray-400">Source-backed note</p>
      <p className="mt-2 text-sm leading-6 text-gray-700">Every answer can jump back to the exact page, timestamp, or image.</p>
    </div>
  </div>
);

const AgentPreview: React.FC<{ feature: DemoFeature; activeStep: number }> = ({ feature, activeStep }) => (
  <div className="space-y-3">
    <div className="rounded-lg bg-gray-950 p-4 text-white">
      <p className="text-xs font-bold text-blue-200">Agent request</p>
      <p className="mt-2 text-sm leading-6">Plan my assignment from the lecture and the PDF. Show sources before drafting.</p>
    </div>

    <div className="rounded-lg bg-white p-4 ring-1 ring-gray-200">
      {feature.steps.map((step, index) => (
        <div key={step.title} className="flex gap-3 pb-3 last:pb-0">
          <span
            className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
            style={{ backgroundColor: index <= activeStep ? feature.color : "#cbd5e1" }}
          >
            {index + 1}
          </span>
          <div>
            <p className="text-sm font-bold text-gray-950">{step.title}</p>
            <p className="mt-1 text-xs leading-5 text-gray-500">{step.body}</p>
          </div>
        </div>
      ))}
    </div>

    <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
      <p className="text-sm font-bold text-gray-950">Ready for approval</p>
      <p className="mt-2 text-sm leading-6 text-gray-600">Outline, source list, and next actions are waiting before anything is sent or created.</p>
    </div>
  </div>
);

const CreatePreview: React.FC<{ feature: DemoFeature; activeStep: number }> = ({ feature, activeStep }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-2">
      {[
        ["lucide:file-text", "Summary"],
        ["lucide:table-2", "Data table"],
        ["lucide:map", "Mind map"],
        ["lucide:pen-line", "Draft"],
      ].map(([icon, label], index) => (
        <div
          key={label}
          className={`rounded-lg border p-3 ${index === activeStep + 1 ? "border-orange-300 bg-orange-50" : "border-gray-200 bg-white"}`}
        >
          <Icon icon={icon} className="text-lg" style={{ color: feature.color }} />
          <p className="mt-2 text-xs font-bold text-gray-950">{label}</p>
        </div>
      ))}
    </div>

    <div className="rounded-lg bg-white p-4 ring-1 ring-gray-200">
      <p className="text-xs font-bold text-gray-400">Generated draft</p>
      <h4 className="mt-2 text-base font-bold text-gray-950">Essay outline: AI in education</h4>
      <div className="mt-3 space-y-2 text-sm leading-6 text-gray-600">
        <p>1. Claim: tutoring works best when grounded in class material.</p>
        <p>2. Evidence: lecture timestamp and PDF section citations attached.</p>
      </div>
    </div>

    <div className="rounded-lg border border-gray-200 bg-[#fff7ed] p-4">
      <p className="text-sm font-bold text-gray-950">Editable before export</p>
      <p className="mt-2 text-sm leading-6 text-gray-600">Keep the structure, rewrite the tone, or ask YouWo for another version.</p>
    </div>
  </div>
);

const StudyPreview: React.FC<{ feature: DemoFeature; activeStep: number }> = ({ feature, activeStep }) => (
  <div className="space-y-3">
    <div className="rounded-lg bg-white p-4 ring-1 ring-gray-200">
      <p className="text-xs font-bold text-gray-400">Quiz</p>
      <h4 className="mt-2 text-sm font-bold leading-6 text-gray-950">Which concept explains why active recall improves retention?</h4>
      <div className="mt-3 space-y-2">
        {["Passive review", "Retrieval practice", "Highlighting", "Re-reading"].map((answer, index) => (
          <div
            key={answer}
            className={`rounded-lg border px-3 py-2 text-sm ${
              index === 1 && activeStep >= 1 ? "border-green-300 bg-green-50 text-green-800" : "border-gray-200 bg-[#f8fafc] text-gray-600"
            }`}
          >
            {answer}
          </div>
        ))}
      </div>
    </div>

    <div className={`rounded-lg border p-4 ${activeStep >= 2 ? "border-green-300 bg-green-50" : "border-gray-200 bg-white"}`}>
      <p className="text-xs font-bold text-gray-400">Flashcard</p>
      <p className="mt-2 text-sm font-bold text-gray-950">What is retrieval practice?</p>
      <p className="mt-3 text-sm leading-6 text-gray-600">
        Actively pulling information from memory, then checking it against the source.
      </p>
    </div>

    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-sm font-bold text-gray-950">Review plan</p>
      <p className="mt-2 text-sm leading-6 text-gray-600">Weak topics become tomorrow's quiz and flashcard queue.</p>
    </div>
  </div>
);

const fallbackCopy: Record<FeatureKey, Omit<DemoFeature, "key" | "icon" | "color" | "softColor">> = {
  record: {
    label: "Record",
    title: "Record a class or meeting and keep thinking.",
    kicker: "Live transcript, translation, and questions while you listen.",
    body: "YouWo captures the conversation, keeps timestamps, and turns the live transcript into source-backed notes.",
    prompt: "Record this lecture, translate key ideas, and mark anything that could appear on the exam.",
    sources: ["Live audio", "Transcript", "Class language"],
    steps: [
      { title: "Start recording", body: "Tap once and let the transcript stream while you stay present." },
      { title: "Capture meaning", body: "Important moments, translations, and timestamps stay connected." },
      { title: "Ask in context", body: "Ask for definitions, examples, or follow-up notes before the recording ends." },
    ],
    outputs: ["Clean transcript", "Translated notes", "Source-linked summary"],
  },
  import: {
    label: "Import",
    title: "Drop in anything you already have.",
    kicker: "PDFs, YouTube, audio, images, docs, and links become usable context.",
    body: "Imports become structured notes with sections, citations, and searchable context for chat, creation, and study.",
    prompt: "Import this chapter, lecture video, and whiteboard image. Build one note I can ask questions from.",
    sources: ["PDF", "YouTube", "Image"],
    steps: [
      { title: "Add sources", body: "Upload or paste the materials you need for one topic." },
      { title: "Structure them", body: "YouWo extracts sections, timestamps, key terms, and citations." },
      { title: "Make it searchable", body: "Everything becomes one note that the agent can reason over." },
    ],
    outputs: ["Merged source note", "Clickable citations", "Topic sections"],
  },
  agent: {
    label: "Agent",
    title: "Ask an agent to do the next step.",
    kicker: "The agent works from your notes, files, recordings, and approvals.",
    body: "Instead of a blank chat, the agent receives your captured context and proposes useful work you can review.",
    prompt: "Use my lecture notes and rubric to plan the assignment, then show me what to approve.",
    sources: ["Rubric", "Lecture", "Saved notes"],
    steps: [
      { title: "Read the context", body: "The agent pulls the relevant notes, sources, and requirements." },
      { title: "Plan the work", body: "It drafts a path forward with source links and assumptions." },
      { title: "Wait for approval", body: "You review, edit, or run the next action." },
    ],
    outputs: ["Assignment plan", "Task list", "Approval-ready draft"],
  },
  create: {
    label: "Create",
    title: "Turn captured material into finished drafts.",
    kicker: "Summaries, outlines, reports, tables, notes, and drafts from the same sources.",
    body: "Creation starts from evidence, so the output is easier to trust, edit, export, and reuse.",
    prompt: "Create an essay outline and a study summary from these sources. Keep citations beside each claim.",
    sources: ["Transcript", "PDF", "Prompt"],
    steps: [
      { title: "Choose an output", body: "Pick a summary, table, mind map, draft, or custom prompt." },
      { title: "Generate with evidence", body: "YouWo writes from the exact sources you selected." },
      { title: "Edit and export", body: "Refine the result or turn it into another format." },
    ],
    outputs: ["Essay outline", "Study summary", "Citation-backed draft"],
  },
  study: {
    label: "Study",
    title: "Study from the exact material you captured.",
    kicker: "Quizzes, flashcards, explanations, and review plans from your own sources.",
    body: "YouWo turns notes into active recall, then points you back to the source when you miss something.",
    prompt: "Build a quiz and flashcards from this lecture. Focus on the ideas I struggled with.",
    sources: ["Notes", "Sections", "Weak topics"],
    steps: [
      { title: "Generate practice", body: "Create quiz questions and flashcards from the note." },
      { title: "Answer with feedback", body: "Get explanations grounded in the original source." },
      { title: "Review what matters", body: "Weak topics become the next practice queue." },
    ],
    outputs: ["Practice quiz", "Flashcard set", "Review queue"],
  },
};

export default ConsumerWorkflowDemo;
